/** @typedef {'intent' | 'view' | 'timing' | 'error' | 'event'} TrackEventType */

/**
 * @typedef {object} DigitalDataEventParams
 * @property {object} eventInfo
 * @property {string} eventInfo.eventAction
 * @property {TrackEventType} [eventInfo.type]
 * @property {number} [eventInfo.timeStamp]
 * @property {string} componentID
 * @property {object} attributes
 */

/**
 * Push a new event info DigitalData CEDDL
 *
 * @param {DigitalDataEventParams} params Event name or event detail
 */
export default function track({
  eventInfo: { eventAction, type = 'intent', timeStamp = Date.now(), ...eventInfo } = {},
  componentID = undefined,
  attributes = {},
}) {
  window.digitalData?.event.push({
    eventInfo: {
      eventAction,
      type,
      timeStamp: new Date(timeStamp).toJSON(),
      ...eventInfo,
    },
    attributes: structuredClone(attributes),
    ...(componentID != null && { componentID }),
  });
}

/**
 * @param {TrackEventType} type
 * @param {string} action Event action
 * @param {string} componentID Component identifier
 * @param {object} attributes Event attributes (product infos, component ID, etc.)
 */
function alias(type, action, componentID = undefined, attributes = undefined) {
  track({ eventInfo: { eventAction: action, type }, componentID, attributes });
}

/**
 * Track user interaction: request from user (click, submit, select, add, remove, open, etc.) that doesn't always
 * succeed to a result (change)
 *
 * @function
 * @param {string} name Event name
 * @param {object} attributes Event attributes (product infos, component ID, etc.)
 */
export const trackIntent = /*#__PURE__*/ alias.bind(this, 'intent');

/**
 * Track view changes (showing new content) about the UI / view (page, tab panel, dialog, disclosure, etc.)
 *
 * @function
 * @param {string} name Event name
 * @param {object} attributes Event attributes (product infos, component ID, etc.)
 */
export const trackView = /*#__PURE__*/ alias.bind(this, 'view');

/**
 * Track errors or exceptions (fatal or not)
 *
 * @function
 * @param {string} name Event name
 * @param {object} attributes Event attributes (product infos, component ID, etc.)
 */
export const trackError = /*#__PURE__*/ alias.bind(this, 'error');

/**
 * Track other events that doesn't fall in other types (application, debug, warning, etc.)
 *
 * @function
 * @param {string} name Event name
 * @param {object} attributes Event attributes (product infos, component ID, etc.)
 */
export const trackEvent = /*#__PURE__*/ alias.bind(this, 'event');

/**
 * Track metrics events for measurements (user, navigation, resources, etc.)
 *
 * @function
 * @param {string} name Event name
 * @param {object} attributes Event attributes (product infos, component ID, etc.)
 */
export const trackTiming = /*#__PURE__*/ alias.bind(this, 'timing');
